import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormError } from "@/pages/auth/AuthShell";
import { api, errorMessage } from "@/lib/api";
import { IP_TYPES } from "@/lib/format";

const EMPTY = { asset_type: "us_trademark", title: "", jurisdiction: "US", owner: "", number: "", key_date: "", original_grant_date: "", source_url: "", verification_status: "customer_supplied", notices: "", maintenance_history: "" };

export const IpAssetDialog = ({ workspaceId, asset, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({ ...EMPTY, ...asset, key_date: asset?.key_date || "", original_grant_date: asset?.original_grant_date || "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));
  const isPatent = form.asset_type.includes("patent");

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    const body = { ...form, key_date: form.key_date || null, original_grant_date: form.asset_type === "us_reissue_patent" ? form.original_grant_date || null : null };
    ["id", "workspace_id", "created_by", "created_at", "updated_at"].forEach((k) => delete body[k]);
    try {
      if (asset) await api.patch(`/workspaces/${workspaceId}/ip-assets/${asset.id}`, body);
      else await api.post(`/workspaces/${workspaceId}/ip-assets`, body);
      onOpenChange(false); onSaved();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  const text = (k, label, props = {}) => (
    <div className="space-y-2"><Label htmlFor={`ip-${k}`}>{label}</Label><Input id={`ip-${k}`} data-testid={`ip-${k}-input`} value={form[k]} onChange={set(k)} className="h-11" {...props} /></div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ip-asset-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{asset ? "Edit record" : "Add a trademark or patent"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label>Type</Label>
            <Select value={form.asset_type} onValueChange={set("asset_type")}><SelectTrigger data-testid="ip-type-select" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(IP_TYPES).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`ip-type-${v}`}>{l}</SelectItem>)}</SelectContent></Select></div>
          {text("title", isPatent ? "Invention title" : "Mark or brand name", { required: true })}
          {text("owner", "Owner (as shown on the official record)", { required: true })}
          <div className="grid gap-4 sm:grid-cols-2">{text("jurisdiction", "Jurisdiction")}{text("number", isPatent ? "Patent number" : "Registration number")}</div>
          {text("key_date", isPatent ? "Grant date" : "Registration date", { type: "date" })}
          {form.asset_type === "us_reissue_patent" && text("original_grant_date", "Original patent's grant date", { type: "date" })}
          {text("source_url", "Link to the official record (https)", { placeholder: "https://..." })}
          <div className="space-y-2"><Label>Have you checked these details against the official record?</Label>
            <Select value={form.verification_status} onValueChange={set("verification_status")}><SelectTrigger data-testid="ip-verification-select" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="customer_supplied">Not yet: entered from my own notes</SelectItem><SelectItem value="customer_checked_official_record">Yes: I checked the official record</SelectItem></SelectContent></Select></div>
          <div className="space-y-2"><Label htmlFor="ip-notices">Official notices you've received (optional)</Label><Textarea id="ip-notices" data-testid="ip-notices-input" value={form.notices} onChange={set("notices")} rows={2} /></div>
          <div className="space-y-2"><Label htmlFor="ip-history">Filings or fees already done (optional)</Label><Textarea id="ip-history" data-testid="ip-history-input" value={form.maintenance_history} onChange={set("maintenance_history")} rows={2} /></div>
          <FormError message={error} testId="ip-form-error" />
          <Button type="submit" disabled={busy} data-testid="ip-save-button" className="h-11 w-full bg-navy hover:bg-navy-2">{busy ? "Saving..." : "Save record"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
